import { getTranslation } from 'helpers/getTranslation';

import {
  eventNameToTranslationKey,
  getAnalyticsEventBaseName,
} from './analyticsEventUtils';
import { resolveCatalogParentLabel } from './catalogContextDisplay';
import {
  describeClientErrorBriefly,
  getEndpointWhen,
} from './journeyEndpointLabels';
import {
  SCREEN_NAME_MAX_LENGTH,
  SESSIONS_LOOKBACK_DAYS,
  buildExitScreenStats,
  capitalizeJourneyText,
  describeCatalogParentInSentence,
  getEventTitle,
  getScreenLabel,
  lowercaseJourneyText,
  readJourneyCatalogId,
  stripCatalogKindPrefix,
  truncateJourneyText,
  type AnalyticsStudentSessionDetailDto,
  type ExitCause,
  type ExitScreenStat,
  type SessionEvent,
  type SessionScreen,
  type SessionScreenRef,
  type SessionSummary,
} from './TimelineSection.helpers';
import { buildChapterScreens, countSolvedTasks } from './journeyEventLog';

/*
 * «Коротко о сессии»: one or two sentences built from templates (no AI) — how
 * the student came in, where they went and what they got done. How the session
 * ended is the verdict's job: it sits right below and is not repeated here.
 */

const STORY_PREFIX = 'student_page.journey.story';
const EXIT_SCREENS_PREFIX = 'student_page.journey.exit_screens';

/** Places listed by name before «и ещё N». */
const STORY_PLACES_LIMIT = 3;
/** `App Opened` / `Plugin Opened` in the first seconds means the session began with opening. */
const STORY_OPENER_WINDOW_MS = 2 * 1000;

const OPENER_EVENTS: Record<string, 'plugin' | 'site'> = {
  'Plugin Opened': 'plugin',
  'App Opened': 'site',
};

/** Entry steps worth a clause, in the student's own words (the log titles). */
const MILESTONE_EVENT_NAMES = new Set([
  'Plugin Intro Completed',
  'Plugin Intro Skipped',
  'Signup Completed',
  'Login Completed',
  'Profile Setup Submitted',
  'Onboarding Completed',
]);

type StoryOptions = { studentEmail?: string | null };

type PlaceKind = 'lesson' | 'trainer' | 'training' | 'arcade' | 'course';

type StoryPlace = {
  kind: PlaceKind;
  name: string | null;
  /** An unnamed lesson: `name` is its trainer / training, said as «в тренажёре «…»». */
  inParent?: string | null;
};

const readNonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

/** «A», «A и B», «A, B и C». */
const joinList = (items: string[]): string => {
  if (items.length <= 1) {
    return items[0] ?? '';
  }

  return getTranslation(`${STORY_PREFIX}.list_and`, {
    items: items.slice(0, -1).join(', '),
    last: items[items.length - 1],
  });
};

const toSentence = (text: string) => `${capitalizeJourneyText(text)}.`;

/**
 * «заполнил профиль», «заполнил анкету» → «заполнил профиль», «анкету», so
 * the list reads «… заполнил профиль и анкету». Only when two neighbours
 * differ in their last word alone (the verb phrase is the same).
 */
export const dropRepeatedLeadWords = (items: string[]): string[] =>
  items.map((item, index) => {
    const words = item.split(' ');
    const previousWords = index > 0 ? items[index - 1].split(' ') : [];
    const lead = words.length - 1;

    if (
      lead === 0 ||
      previousWords.length !== words.length ||
      words.slice(0, lead).join(' ') !== previousWords.slice(0, lead).join(' ')
    ) {
      return item;
    }

    return words[lead];
  });

/* Opening and milestones */

const findOpener = (events: SessionEvent[]): 'plugin' | 'site' | null => {
  const startMs = events[0]?.offsetMs ?? 0;
  const opener = events.find(
    (event) =>
      event.offsetMs - startMs <= STORY_OPENER_WINDOW_MS &&
      OPENER_EVENTS[getAnalyticsEventBaseName(event.eventName)] != null,
  );

  return opener
    ? OPENER_EVENTS[getAnalyticsEventBaseName(opener.eventName)]
    : null;
};

/** «прошёл вводную практику», «вошёл через Google» — once each; a sign-up makes the login redundant. */
const collectMilestones = (
  events: SessionEvent[],
  { studentEmail }: StoryOptions,
): string[] => {
  const seen = new Set<string>();
  const hasSignup = events.some(
    (event) =>
      getAnalyticsEventBaseName(event.eventName) === 'Signup Completed',
  );

  return events.reduce<string[]>((milestones, event) => {
    const baseName = getAnalyticsEventBaseName(event.eventName);

    if (
      !MILESTONE_EVENT_NAMES.has(baseName) ||
      seen.has(baseName) ||
      (hasSignup && baseName === 'Login Completed')
    ) {
      return milestones;
    }

    seen.add(baseName);

    return [
      ...milestones,
      lowercaseJourneyText(getEventTitle(event, studentEmail)),
    ];
  }, []);
};

/* Places */

const CATALOG_SCREEN_PLACES: Record<string, PlaceKind> = {
  trainer_viewed: 'trainer',
  training_viewed: 'training',
  arcade_viewed: 'arcade',
  course_viewed: 'course',
};

const readScreenPlace = (
  screen: Pick<SessionScreen, 'eventName' | 'properties'>,
): StoryPlace | null => {
  if (!screen.eventName) {
    return null;
  }

  const properties = screen.properties ?? {};
  const translationKey = eventNameToTranslationKey(screen.eventName);

  if (translationKey === 'lesson_viewed') {
    const lessonName = readNonEmptyString(properties.lesson_name);
    const parentName = resolveCatalogParentLabel(properties);

    if (lessonName) {
      return { kind: 'lesson', name: lessonName };
    }

    return {
      kind: 'lesson',
      name: parentName,
      inParent: describeCatalogParentInSentence(
        properties,
        SCREEN_NAME_MAX_LENGTH,
      )?.inPhrase,
    };
  }

  const kind = CATALOG_SCREEN_PLACES[translationKey];

  if (!kind) {
    return null;
  }

  const ownName =
    kind === 'training'
      ? readNonEmptyString(properties.training_name)
      : kind === 'arcade'
      ? readNonEmptyString(properties.arcade_name)
      : kind === 'course'
      ? readNonEmptyString(properties.course_name)
      : null;

  return {
    kind,
    name:
      ownName ??
      (kind === 'trainer'
        ? resolveCatalogParentLabel(properties)
        : readNonEmptyString(properties.module_name)),
  };
};

const formatPlace = (place: StoryPlace): string => {
  if (!place.name) {
    return getTranslation(`${STORY_PREFIX}.places.${place.kind}_unnamed`);
  }

  /* «урок в тренажёре «Auto Layout в Figma»», as the verdict names it. */
  if (place.inParent) {
    return getTranslation(`${STORY_PREFIX}.places.lesson_in_parent`, {
      parent: place.inParent,
    });
  }

  /* «тренажёр «Auto Layout в Figma»» — the kind is said by the noun before the name. */
  return getTranslation(`${STORY_PREFIX}.places.${place.kind}`, {
    name: truncateJourneyText(
      place.kind === 'lesson'
        ? place.name
        : stripCatalogKindPrefix(place.kind, place.name),
      SCREEN_NAME_MAX_LENGTH,
    ),
  });
};

/**
 * Catalog places in the order they were opened, once each. An unnamed lesson
 * («урок в «Тренинг…»») is left out when its training is already listed.
 */
const collectPlaces = (screens: SessionScreen[]): string[] => {
  const seen = new Set<string>();
  const names = new Set<string>();
  const places = buildChapterScreens(screens)
    .map(readScreenPlace)
    .filter((place): place is StoryPlace => {
      const key = place
        ? `${place.kind}\u0000${place.inParent ? 'in' : ''}\u0000${
            place.name ?? ''
          }`
        : null;

      if (
        !place ||
        !key ||
        seen.has(key) ||
        (place.inParent && place.name != null && names.has(place.name))
      ) {
        return false;
      }

      seen.add(key);

      if (place.name) {
        names.add(place.name);
      }

      return true;
    })
    .map(formatPlace);

  return places.length > STORY_PLACES_LIMIT
    ? [
        ...places.slice(0, STORY_PLACES_LIMIT),
        getTranslation(`${STORY_PREFIX}.places.more`, {
          count: places.length - STORY_PLACES_LIMIT,
        }),
      ]
    : places;
};

/** Other screens by name when no lesson or trainer was opened: «справочник», «профиль». */
const collectScreenNames = (screens: SessionScreen[]): string[] => {
  const names = buildChapterScreens(screens)
    .filter(
      (screen) =>
        screen.eventName &&
        !OPENER_EVENTS[getAnalyticsEventBaseName(screen.eventName)],
    )
    .map((screen) =>
      lowercaseJourneyText(
        getScreenLabel(screen.eventName, screen.properties, {
          maxNameLength: SCREEN_NAME_MAX_LENGTH,
          inSentence: true,
        }),
      ),
    );

  return [...new Set(names)];
};

/* Results */

const countDistinct = (
  events: SessionEvent[],
  baseName: string,
  readKey: (event: SessionEvent) => string | null,
) =>
  new Set(
    events
      .filter(
        (event) => getAnalyticsEventBaseName(event.eventName) === baseName,
      )
      .map((event, position) => readKey(event) ?? `#${position}`),
  ).size;

/** «решил 3 задачи», «прошёл урок и модуль», «оплатил подписку». */
const collectResults = (events: SessionEvent[]): string[] => {
  const results: string[] = [];
  /* The same counter as the chapter headers («решил 1 задачу» in both); intro tasks are the intro. */
  const tasks = countSolvedTasks(events, { includeIntro: false }).solved;
  const lessons = countDistinct(events, 'Lesson Completed', (event) =>
    readJourneyCatalogId(event.properties?.lesson_id),
  );
  const modules = countDistinct(events, 'Module Completed', (event) =>
    readJourneyCatalogId(event.properties?.module_id),
  );
  const completed = [
    lessons === 1
      ? getTranslation(`${STORY_PREFIX}.completed_lesson`)
      : lessons > 1
      ? getTranslation(`${STORY_PREFIX}.completed_lessons`, { count: lessons })
      : null,
    modules === 1
      ? getTranslation(`${STORY_PREFIX}.completed_module`)
      : modules > 1
      ? getTranslation(`${STORY_PREFIX}.completed_modules`, { count: modules })
      : null,
  ].filter((part): part is string => Boolean(part));

  if (tasks > 0) {
    results.push(
      getTranslation(`${STORY_PREFIX}.tasks_solved`, { count: tasks }),
    );
  }

  if (completed.length > 0) {
    results.push(
      getTranslation(`${STORY_PREFIX}.completed`, {
        items: joinList(completed),
      }),
    );
  }

  if (
    events.some(
      (event) =>
        getAnalyticsEventBaseName(event.eventName) === 'Payment Succeeded',
    )
  ) {
    results.push(getTranslation(`${STORY_PREFIX}.paid`));
  }

  return results;
};

/**
 * «Коротко о сессии» in 1–2 sentences: how the session began (opening, sign-in,
 * intro), where the student went and what they got done. The ending — the
 * problem, the exit — is left to the verdict right below it.
 */
export const buildSessionStory = (
  detail: AnalyticsStudentSessionDetailDto,
  options: StoryOptions = {},
): string[] => {
  if (detail.events.length === 0) {
    return [];
  }

  const opener = findOpener(detail.events);
  const milestones = collectMilestones(detail.events, options);
  const places = collectPlaces(detail.screens);
  const results = collectResults(detail.events);
  const openerAction = opener
    ? getTranslation(`${STORY_PREFIX}.opened_${opener}`)
    : null;
  const sentences: string[] = [];

  if (milestones.length > 0) {
    sentences.push(
      toSentence(
        joinList(
          dropRepeatedLeadWords([
            ...(openerAction ? [openerAction] : []),
            ...milestones,
          ]),
        ),
      ),
    );
  }

  /* Without milestones the opening joins the places: «Открыл плагин и урок «…»». */
  const openerLead = milestones.length === 0 ? openerAction : null;
  let activity: string | null = null;

  if (places.length > 0) {
    const opened = getTranslation(
      `${STORY_PREFIX}.${milestones.length > 0 ? 'then_opened' : 'opened'}`,
      {
        items: joinList([
          ...(openerLead && opener
            ? [getTranslation(`${STORY_PREFIX}.objects.${opener}`)]
            : []),
          ...places,
        ]),
      },
    );

    /* «Открыл урок «X», решил 2 задачи и прошёл урок». */
    activity = results.length > 0 ? `${opened}, ${joinList(results)}` : opened;
  } else if (results.length > 0) {
    activity = joinList([...(openerLead ? [openerLead] : []), ...results]);
  } else {
    const screenNames = collectScreenNames(detail.screens);
    const visited =
      screenNames.length > 0
        ? getTranslation(`${STORY_PREFIX}.visited`, {
            count: screenNames.length,
            items: screenNames.join(', '),
          })
        : null;

    activity = visited
      ? joinList([...(openerLead ? [openerLead] : []), visited])
      : openerLead;
  }

  if (activity) {
    sentences.push(toSentence(activity));
  }

  return sentences;
};

/* Exit screens */

export type ExitScreenProblem = {
  cause: Extract<ExitCause, 'error' | 'stuck'>;
  count: number;
  text: string;
};

/**
 * What went wrong, as a lowercase phrase: «ошибка при загрузке урока», «экран
 * сломался», «долго ждал ответа сервера при загрузке урока», «урок долго
 * открывался в Figma», «оплата не прошла».
 */
export const describeExitProblem = (event: SessionScreenRef): string => {
  const properties = event.properties ?? {};
  const baseName = getAnalyticsEventBaseName(event.eventName);
  const when = () =>
    getEndpointWhen(
      readNonEmptyString(properties.method),
      readNonEmptyString(properties.endpoint),
    );

  switch (baseName) {
    case 'API Error':
      return getTranslation(`${EXIT_SCREENS_PREFIX}.problem_api`, {
        when: when(),
      });
    case 'Slow Request':
      return lowercaseJourneyText(
        getTranslation('student_page.journey.verdict.slow_request_what', {
          when: when(),
        }),
      );
    case 'Client Error':
      return describeClientErrorBriefly(properties);
    case 'Rage Click':
      return getTranslation(`${EXIT_SCREENS_PREFIX}.problem_rage`);
    default:
      return lowercaseJourneyText(getEventTitle(event));
  }
};

/**
 * The main problem of an exit screen, under its bar: «ошибка при загрузке урока ·
 * 2 раза» when admin-api sends `exitProblem`, else just the cause — «ушёл из‑за
 * ошибки · 2 раза» / «застрял и ушёл · 1 раз» (errors win a tie); `null` when
 * nothing went wrong there.
 */
export const getExitScreenProblem = (
  stat: Pick<ExitScreenStat, 'byCause' | 'problems'>,
): ExitScreenProblem | null => {
  const [topProblem] = stat.problems ?? [];

  if (topProblem) {
    return {
      cause: topProblem.cause,
      count: topProblem.count,
      text: getTranslation(`${EXIT_SCREENS_PREFIX}.problem_named`, {
        problem: describeExitProblem(topProblem.event),
        count: topProblem.count,
      }),
    };
  }

  const errors = stat.byCause?.error ?? 0;
  const stuck = stat.byCause?.stuck ?? 0;

  if (errors === 0 && stuck === 0) {
    return null;
  }

  const cause = errors >= stuck ? 'error' : 'stuck';
  const count = cause === 'error' ? errors : stuck;

  return {
    cause,
    count,
    text: getTranslation(`${EXIT_SCREENS_PREFIX}.problem_${cause}`, {
      count,
    }),
  };
};

/* The student over the whole period (JourneySummary) */

const STUDENT_STORY_PREFIX = 'student_page.journey.student_story';

/** A platform with at least this share of the sessions is «чаще в плагине». */
const MOSTLY_PLATFORM_SHARE = 0.6;

/** «чаще в плагине», «все на сайте», «и на сайте, и в плагине». */
const describePlatformMix = (sessions: SessionSummary[]): string => {
  const plugin = sessions.filter(
    (session) => session.platform === 'Plugin',
  ).length;
  const web = sessions.length - plugin;
  let mix = 'even';

  if (sessions.length === 1) {
    mix = plugin === 1 ? 'single_plugin' : 'single_web';
  } else if (web === 0 || plugin === 0) {
    mix = web === 0 ? 'only_plugin' : 'only_web';
  } else if (plugin / sessions.length >= MOSTLY_PLATFORM_SHARE) {
    mix = 'mostly_plugin';
  } else if (web / sessions.length >= MOSTLY_PLATFORM_SHARE) {
    mix = 'mostly_web';
  }

  return getTranslation(`${STUDENT_STORY_PREFIX}.platforms.${mix}`);
};

/**
 * «3 раза ушёл из‑за ошибки — каждый раз на экране: урок «X» (ошибка при
 * загрузке урока)»: where the exits of one cause gather, when they do.
 */
const describeExitConcentration = (
  sessions: SessionSummary[],
  cause: Extract<ExitCause, 'error' | 'stuck'>,
): string | null => {
  const matching = sessions.filter((session) => session.exitCause === cause);

  if (matching.length === 0) {
    return null;
  }

  const lead = getTranslation(`${STUDENT_STORY_PREFIX}.left_${cause}`, {
    count: matching.length,
  });
  const [top] = buildExitScreenStats(matching, 1);
  let placement: string | null = null;

  if (top && matching.length === 1) {
    placement = 'place_single';
  } else if (top && top.total === matching.length) {
    placement = 'place_every';
  } else if (top && top.total >= 2 && top.total * 2 > matching.length) {
    placement = 'place_most';
  }

  if (!top || !placement) {
    return lead;
  }

  const text = getTranslation(`${STUDENT_STORY_PREFIX}.${placement}`, {
    lead,
    top: top.total,
    screen: lowercaseJourneyText(
      getScreenLabel(top.eventName, top.properties, {
        maxNameLength: SCREEN_NAME_MAX_LENGTH,
        inSentence: true,
      }),
    ),
  });
  const [problem] = top.problems ?? [];

  return problem
    ? getTranslation(`${STUDENT_STORY_PREFIX}.with_problem`, {
        text,
        problem: describeExitProblem(problem.event),
      })
    : text;
};

/**
 * What the summary tiles cannot say, in one or two sentences: «За 90 дней — 12
 * сессий, чаще в плагине. 3 раза ушёл из‑за ошибки — каждый раз на экране: урок
 * «Auto Layout…».» Empty when it would only repeat the tiles (no platform mix to
 * tell and no problems).
 */
/**
 * Error and stuck exits that all happened on one screen: one sentence instead
 * of two naming the same screen — «1 раз ушёл из‑за ошибки, 1 раз застрял и
 * ушёл — каждый раз на экране: урок «X»». `null` when they are spread out or a
 * known problem has to be named per cause.
 */
const describeSharedProblemScreen = (
  sessions: SessionSummary[],
): string | null => {
  const errorCount = sessions.filter(
    (session) => session.exitCause === 'error',
  ).length;
  const stuckCount = sessions.filter(
    (session) => session.exitCause === 'stuck',
  ).length;

  if (errorCount === 0 || stuckCount === 0) {
    return null;
  }

  const [top] = buildExitScreenStats(
    sessions.filter(
      (session) =>
        session.exitCause === 'error' || session.exitCause === 'stuck',
    ),
    1,
  );

  if (!top || top.total !== errorCount + stuckCount || top.problems?.length) {
    return null;
  }

  return getTranslation(`${STUDENT_STORY_PREFIX}.place_same`, {
    error: getTranslation(`${STUDENT_STORY_PREFIX}.left_error`, {
      count: errorCount,
    }),
    stuck: getTranslation(`${STUDENT_STORY_PREFIX}.left_stuck`, {
      count: stuckCount,
    }),
    screen: lowercaseJourneyText(
      getScreenLabel(top.eventName, top.properties, {
        maxNameLength: SCREEN_NAME_MAX_LENGTH,
        inSentence: true,
      }),
    ),
  });
};

export const buildStudentJourneyStory = (
  sessions: SessionSummary[],
  { showPlatform = true }: { showPlatform?: boolean } = {},
): string[] => {
  if (sessions.length === 0) {
    return [];
  }

  const sharedProblemScreen = describeSharedProblemScreen(sessions);
  const problems = sharedProblemScreen
    ? [sharedProblemScreen]
    : [
        describeExitConcentration(sessions, 'error'),
        describeExitConcentration(sessions, 'stuck'),
      ].filter((problem): problem is string => Boolean(problem));

  if (!showPlatform && problems.length === 0) {
    return [];
  }

  const params = {
    days: SESSIONS_LOOKBACK_DAYS,
    sessions: getTranslation(`${STUDENT_STORY_PREFIX}.sessions`, {
      count: sessions.length,
    }),
  };
  const intro = showPlatform
    ? getTranslation(`${STUDENT_STORY_PREFIX}.intro_platform`, {
        ...params,
        platform: describePlatformMix(sessions),
      })
    : getTranslation(`${STUDENT_STORY_PREFIX}.intro`, params);

  return [toSentence(intro), ...problems.map(toSentence)];
};
